import type { PAPEvent } from "@mirage/analytics/events/base/event";
import { PAP_Click_AddSourcesButton } from "@mirage/analytics/events/types/click_add_sources_button";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import {
  appendManualMessages,
  postUserMessage,
} from "@mirage/service-conversation";
import type {
  NewUserMessage,
  UserMessageOptions,
} from "@mirage/service-conversation/types";
import { openAddSourceModal } from "@mirage/service-fss-bridge";
import { useDashActionSurface } from "@mirage/shared/hooks/useDashActionSurface";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import { useCallback, useState } from "react";

export type AutoMessagesOnFirstSource =
  | {
      // messages that should be automatically added to the conversation without triggering a response
      prependMessages: NewUserMessage[];
      // message that should be triggered immediately
      newMessage: NewUserMessage | undefined;
      // user message options that should be set after the messages are added
      userMessageOptions: UserMessageOptions | undefined;
    }
  | undefined;
export type GetAutoMessagesOnFirstSource =
  () => Promise<AutoMessagesOnFirstSource>;

export function useAddSources(
  logComposeEvent: (event: PAPEvent) => void,
  getAutoMessagesOnFirstSource: () => Promise<AutoMessagesOnFirstSource>,
  manuallyAddedSources: ChatSource[],
  setManuallyAddedSources: (sources: ChatSource[]) => void,
  setUserMessageOptions: (options: UserMessageOptions) => void,
) {
  const { dashActionSurface } = useDashActionSurface();
  const [isAddSourceModalOpen, setIsAddSourceModalOpen] = useState(false);

  const handleClickAddSources = useCallback(() => {
    if (isEmbedPage()) {
      openAddSourceModal();
      return;
    }
    setIsAddSourceModalOpen(true);
    logComposeEvent(
      PAP_Click_AddSourcesButton({
        actionType: "chat_pane",
        actionSurface: dashActionSurface,
        dashActionSurface,
        actionSurfaceComponent: "compose_chat_pane",
        featureLine: "assist",
      }),
    );
  }, [dashActionSurface, logComposeEvent, setIsAddSourceModalOpen]);

  const handleCloseAddSourcesModal = useCallback(async () => {
    if (manuallyAddedSources.length > 0) {
      const autoMessages = await getAutoMessagesOnFirstSource();
      if (autoMessages) {
        if (autoMessages.prependMessages.length > 0) {
          appendManualMessages([...autoMessages.prependMessages]);
        }
        if (autoMessages.newMessage) {
          postUserMessage(autoMessages.newMessage);
        }
        if (autoMessages.userMessageOptions) {
          setUserMessageOptions(autoMessages.userMessageOptions);
        }
        setManuallyAddedSources([]);
      }
    }
    setIsAddSourceModalOpen(false);
  }, [
    manuallyAddedSources.length,
    getAutoMessagesOnFirstSource,
    setManuallyAddedSources,
    setUserMessageOptions,
  ]);

  return {
    isAddSourceModalOpen,
    setIsAddSourceModalOpen,
    handleClickAddSources,
    handleCloseAddSourcesModal,
  };
}
